import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import type { ExtracaoFaturaRow, FaturaEstruturada, ReconciliacaoResultado, CorrecoesPlanilha } from '../../types';
import { listExtracoesDoLote } from '../../src/services/extracaoApi';

type FileStatus = 'pending' | 'processing' | 'success' | 'error';

const LOTE_KEY = 'vigorhub_extracao_lote_ativo';
const NOMES_KEY = 'vigorhub_extracao_lote_arquivos';

const lerNomes = (): string[] => {
    try { return JSON.parse(localStorage.getItem(NOMES_KEY) || '[]'); } catch { return []; }
};

export interface ProcessedFile {
    file?: File;                   // ausente para arquivos recuperados/perdidos após um reload
    fileName: string;
    status: FileStatus;
    estruturado?: FaturaEstruturada;
    reconciliacao?: ReconciliacaoResultado | null;
    extracao_id?: string;
    pdf_storage_path?: string | null;
    correcoes?: CorrecoesPlanilha | null;   // correções manuais da planilha já gravadas
    error_message?: string;
    recuperado?: boolean;          // true quando reidratado do banco (não veio de upload nesta sessão)
    precisaReenvio?: boolean;      // true = estava na fila antes da queda mas não terminou; sem bytes, precisa ser arrastado de novo
    tentativas?: number;           // nº de tentativas de extração já feitas (auto-retry para em 3)
}

interface ExtracaoFaturasContextType {
    files: ProcessedFile[];
    setFiles: React.Dispatch<React.SetStateAction<ProcessedFile[]>>;
    isProcessing: boolean;
    setIsProcessing: React.Dispatch<React.SetStateAction<boolean>>;
    fileInputRef: React.RefObject<HTMLInputElement | null>;
    loteId: string | null;
    garantirLoteId: () => string;
    resetLote: () => void;
    abrirLote: (id: string) => Promise<number>;
    recuperando: boolean;
    qtdRecuperada: number;
}

/** Converte linhas do banco em ProcessedFile, deduplicando por nome de arquivo:
 *  fica a cópia que confere ('ok'); em empate, a mais recente (rows chegam em
 *  created_at crescente). Sem os bytes do PDF — reprocessos usam o OCR salvo. */
const rowsParaFiles = (rows: ExtracaoFaturaRow[], recuperado: boolean): Map<string, ProcessedFile> => {
    const porNome = new Map<string, ProcessedFile>();
    for (const r of rows) {
        const fileName = r.arquivo_nome || r.uc_codigo || 'fatura.pdf';
        const candidata: ProcessedFile = {
            fileName,
            status: 'success',
            estruturado: r.dados_estruturados || undefined,
            reconciliacao: r.reconciliacao,
            extracao_id: r.id,
            pdf_storage_path: r.pdf_storage_path,
            correcoes: r.correcoes_planilha,
            recuperado,
        };
        const atual = porNome.get(fileName);
        if (!atual || !(atual.reconciliacao?.status === 'ok' && candidata.reconciliacao?.status !== 'ok')) {
            porNome.set(fileName, candidata);
        }
    }
    return porNome;
};

const ExtracaoFaturasContext = createContext<ExtracaoFaturasContextType | null>(null);

export const ExtracaoFaturasProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [files, setFiles] = useState<ProcessedFile[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [loteId, setLoteId] = useState<string | null>(() => localStorage.getItem(LOTE_KEY));
    const [recuperando, setRecuperando] = useState(false);
    const [qtdRecuperada, setQtdRecuperada] = useState(0);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const jaTentouRecuperar = useRef(false);
    // Capturado UMA VEZ no mount, antes que o efeito de sincronização (abaixo)
    // possa sobrescrever a lista com o array vazio do primeiro render.
    const [nomesAntesDaQueda] = useState<string[]>(() => lerNomes());

    // Se caiu o servidor/página no meio de um lote, ao recarregar reidratamos
    // as faturas que JÁ tinham terminado de processar (já estão salvas no banco)
    // E sinalizamos, pelo nome, quais arquivos da fila original ainda faltam —
    // esses não têm como voltar sozinhos (o PDF só existia na memória do
    // navegador), mas ao menos o usuário sabe exatamente o que reenviar.
    useEffect(() => {
        if (jaTentouRecuperar.current || !loteId) return;
        jaTentouRecuperar.current = true;
        setRecuperando(true);
        listExtracoesDoLote(loteId)
            .then(rows => {
                if (rows.length === 0 && nomesAntesDaQueda.length === 0) return;
                // O banco pode ter o MESMO arquivo gravado 2×+ no lote (reenvio após
                // queda de internet duplicava a linha) — o dedup fica no rowsParaFiles.
                const porNome = rowsParaFiles(rows, true);
                const recuperadas = [...porNome.values()];
                // Nome da fila original sem linha no banco = ainda não processou.
                // (Set: filas salvas antes do dedup podiam repetir o mesmo nome.)
                const faltando: ProcessedFile[] = [];
                for (const nome of new Set(nomesAntesDaQueda)) {
                    if (!porNome.has(nome)) faltando.push({
                        fileName: nome,
                        status: 'error',
                        error_message: 'Não terminou antes da queda — arraste este arquivo de novo pra reenviar.',
                        precisaReenvio: true,
                        recuperado: true,
                    });
                }
                // sessão já populada, não sobrescreve
                setFiles(prev => prev.length > 0 ? prev : [...recuperadas, ...faltando]);
                setQtdRecuperada(recuperadas.length);
            })
            .catch(() => { /* lote antigo/inexistente — segue vazio */ })
            .finally(() => setRecuperando(false));
    }, [loteId, nomesAntesDaQueda]);

    // Mantém a lista de nomes em sincronia enquanto o usuário monta a fila —
    // é o que permite, numa próxima queda, saber quais arquivos faltaram.
    useEffect(() => {
        if (!loteId) return;
        localStorage.setItem(NOMES_KEY, JSON.stringify(files.map(f => f.fileName)));
    }, [files, loteId]);

    const garantirLoteId = (): string => {
        if (loteId) return loteId;
        const novo = crypto.randomUUID();
        localStorage.setItem(LOTE_KEY, novo);
        setLoteId(novo);
        return novo;
    };

    const resetLote = () => {
        localStorage.removeItem(LOTE_KEY);
        localStorage.removeItem(NOMES_KEY);
        setLoteId(null);
        setQtdRecuperada(0);
        jaTentouRecuperar.current = true; // evita recuperar de novo até haver um loteId novo
    };

    /** Abre um lote do Histórico na tela de extração: ele vira o lote ativo e a
     *  fila passa a ser as faturas dele (revisar/reprocessar/exportar; PDFs novos
     *  arrastados depois entram no mesmo lote). Devolve quantas faturas carregou. */
    const abrirLote = async (id: string): Promise<number> => {
        const rows = await listExtracoesDoLote(id);
        if (rows.length === 0) throw new Error('Esse lote não tem faturas salvas.');
        const abertas = [...rowsParaFiles(rows, false).values()];
        localStorage.setItem(LOTE_KEY, id);
        jaTentouRecuperar.current = true; // já veio do banco — não precisa recuperar de novo
        setLoteId(id);
        setFiles(abertas);
        setQtdRecuperada(0);
        return abertas.length;
    };

    return (
        <ExtracaoFaturasContext.Provider value={{
            files, setFiles, isProcessing, setIsProcessing, fileInputRef,
            loteId, garantirLoteId, resetLote, abrirLote, recuperando, qtdRecuperada,
        }}>
            {children}
        </ExtracaoFaturasContext.Provider>
    );
};

export const useExtracaoFaturas = (): ExtracaoFaturasContextType => {
    const ctx = useContext(ExtracaoFaturasContext);
    if (!ctx) throw new Error('useExtracaoFaturas must be used within ExtracaoFaturasProvider');
    return ctx;
};
